import React, { useState } from 'react';
import { Organigramme } from '../../../../types/organisation';
import { User, Building2, Crown, ChevronDown, ChevronRight } from 'lucide-react';

interface OrganigrammePyramidProps {
  organigramme: Organigramme;
}

interface EmployeeNode {
  id: string;
  name: string;
  position: string;
  email?: string;
  isManager: boolean;
  level: number;
  children: EmployeeNode[];
  expanded?: boolean;
}

const OrganigrammePyramid: React.FC<OrganigrammePyramidProps> = ({ organigramme }) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeNode | null>(null);

  // Convertir les données de l'organigramme en structure pyramidale
  const buildPyramidStructure = (): EmployeeNode[] => {
    const nodeMap = new Map<string, EmployeeNode>();
    const rootNodes: EmployeeNode[] = [];

    // Créer tous les nœuds
    organigramme.nodes.forEach(node => {
      if (node.data?.employee) {
        const employee = node.data.employee;
        const isManager = node.data.isManager || false;
        
        
        nodeMap.set(node.id, {
          id: node.id,
          name: `${employee.firstName} ${employee.lastName}`,
          position: node.data?.position?.positionName || node.data?.employee?.position?.positionName || 'Collaborateur',
          email: employee.email,
          isManager,
          level: 0, // Sera calculé plus tard
          children: [],
          expanded: false
        });
      }
    });

    // Construire les relations parent-enfant
    organigramme.edges.forEach(edge => {
      const parent = nodeMap.get(edge.from);
      const child = nodeMap.get(edge.to);
      
      if (parent && child) {
        parent.children.push(child);
      }
    });

    // Trouver les nœuds racines (ceux qui n'ont pas de parent)
    const hasParent = new Set<string>();
    organigramme.edges.forEach(edge => {
      hasParent.add(edge.to);
    });

    nodeMap.forEach((node, id) => {
      if (!hasParent.has(id)) {
        rootNodes.push(node);
      }
    });

    // Calculer les niveaux
    const calculateLevels = (nodes: EmployeeNode[], level: number = 0) => {
      nodes.forEach(node => {
        node.level = level;
        if (node.children.length > 0) {
          calculateLevels(node.children, level + 1);
        }
      });
    };

    calculateLevels(rootNodes);
    return rootNodes;
  };

  const pyramidData = buildPyramidStructure();

  const toggleNodeExpansion = (nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getNodeIcon = (node: EmployeeNode) => {
    if (node.isManager) {
      return <Crown className="h-2.5 w-2.5 text-yellow-600 flex-shrink-0" />;
    }
    return <User className="h-2.5 w-2.5 text-blue-600 flex-shrink-0" />;
  };

  const getNodeColor = (node: EmployeeNode) => {
    if (node.isManager) {
      return 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300';
    }
    return 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300';
  };

  const getLevelColor = (level: number) => {
    const colors = [
      'from-purple-100 to-purple-200', // CEO
      'from-blue-100 to-blue-200',     // Directors
      'from-green-100 to-green-200',   // Managers
      'from-gray-100 to-gray-200',     // Employees
    ];
    return colors[Math.min(level, colors.length - 1)];
  };

  const renderNode = (node: EmployeeNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;

    return (
      <div key={`${node.id}-${level}`} className="flex flex-col items-center">
        {/* Nœud */}
        <div 
          className={`
            relative p-4 rounded-xl border-2 shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer
            ${getNodeColor(node)}
            min-w-[160px] max-w-[200px]
          `}
          onDoubleClick={() => {
            setSelectedEmployee(node);
          }}
        >
          {/* Photo de profil (placeholder) */}
          <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center">
            {getNodeIcon(node)}
          </div>
          
          {/* Nom */}
          <h3 className="text-sm font-bold text-gray-900 text-center mb-1">
            {node.name}
          </h3>
          
          {/* Poste */}
          <p className="text-xs text-gray-700 text-center mb-1">
            {node.data?.position?.positionName || node.data?.employee?.position?.positionName || 'Collaborateur'}
          </p>
          
          {/* Email au survol */}
          {node.email && (
            <div className="group relative">
              <div className="w-2 h-2 bg-gray-400 rounded-full mx-auto cursor-help"></div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                {node.email}
              </div>
            </div>
          )}
          
          {/* Badge Manager */}
          {node.isManager && (
            <div className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              M
            </div>
          )}

          {/* Bouton d'expansion */}
          {hasChildren && (
            <button
              onClick={() => toggleNodeExpansion(node.id)}
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-white border-2 border-gray-300 rounded-full p-1 hover:bg-gray-50 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3 text-gray-600" />
              ) : (
                <ChevronRight className="h-3 w-3 text-gray-600" />
              )}
            </button>
          )}
        </div>

        {/* Lignes de connexion et enfants */}
        {hasChildren && isExpanded && (
          <div className="mt-4 flex flex-col items-center">
            {/* Ligne verticale */}
            <div className="w-px h-4 bg-gray-300 mb-2"></div>
            
            {/* Ligne horizontale */}
            <div className="w-full h-px bg-gray-300 mb-4"></div>
            
            {/* Enfants */}
            <div className="flex flex-wrap justify-center gap-4">
              {node.children.map((child, index) => (
                <div key={`${child.id}-${level + 1}-${index}`} className="flex flex-col items-center">
                  {/* Ligne verticale vers l'enfant */}
                  <div className="w-px h-4 bg-gray-300 mb-2"></div>
                  {renderNode(child, level + 1)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (pyramidData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <p>Aucune hiérarchie trouvée</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-max py-6">
        {/* Titre */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Organigramme - {organigramme.site.siteName}
          </h2>
          <p className="text-sm text-gray-600">
            {organigramme.stats.totalEmployees} employés • {organigramme.stats.totalManagers} managers
          </p>
        </div>

        {/* Instructions */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-500">
            Cliquez sur les flèches pour développer/réduire les niveaux
          </p>
        </div>

        {/* Pyramide */}
        <div className="flex justify-center">
          <div className="flex flex-col items-center space-y-6">
            {pyramidData.map((rootNode, index) => (
              <div key={`root-${rootNode.id}-${index}`} className="flex flex-col items-center">
                {renderNode(rootNode, 0)}
              </div>
            ))}
          </div>
        </div>

        {/* Légende */}
        <div className="mt-8 flex justify-center">
          <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Légende</h4>
            <div className="flex items-center space-x-4 text-xs">
              <div className="flex items-center space-x-1">
                <ChevronDown className="h-3 w-3 text-gray-500" />
                <span className="text-gray-600">Développer</span>
              </div>
              <div className="flex items-center space-x-1">
                <ChevronRight className="h-3 w-3 text-gray-500" />
                <span className="text-gray-600">Réduire</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup employé */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Informations employé</h3>
              <button
                onClick={() => setSelectedEmployee(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Nom complet</label>
                <p className="text-gray-900">{selectedEmployee.name}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Poste</label>
                <p className="text-gray-900">{selectedEmployee.position}</p>
              </div>
              
              {selectedEmployee.email && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{selectedEmployee.email}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-gray-700">Statut</label>
                <p className="text-gray-900">
                  {selectedEmployee.isManager ? 'Manager' : 'Collaborateur'}
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedEmployee(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganigrammePyramid;
